import React, { useState, useEffect } from 'react';
import { RecordItem } from '../../models/RecordItem';
import { Button, Input, TextArea, CurrencySelect } from '../ui';
import closeIcon from '../../assets/images/close.png';
import { useThemeContext } from '../../contexts/ThemeContext';

interface EditRecordModalProps {
  record: RecordItem;
  onClose: () => void;
  onUpdate: (id: number, data: Partial<RecordItem>) => Promise<{ success: boolean; error?: string }>;
  isLoading: boolean;
}

const EditRecordModal: React.FC<EditRecordModalProps> = ({ 
  record, 
  onClose, 
  onUpdate, 
  isLoading 
}) => {
  const { isDark } = useThemeContext();
  const [formData, setFormData] = useState<Partial<RecordItem>>({
    record_name: record.record_name || '',
    record_description: record.record_description || '',
    record_currency_code: record.record_currency_code || 'USD',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleInputChange = (field: keyof RecordItem, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.record_name?.trim()) {
      newErrors.record_name = 'Record name is required';
    }

    if (formData.record_name && formData.record_name.length > 100) {
      newErrors.record_name = 'Record name must be less than 100 characters';
    }

    if (formData.record_description && formData.record_description.length > 500) {
      newErrors.record_description = 'Description must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const result = await onUpdate(record.id!, {
      record_name: formData.record_name,
      record_description: formData.record_description,
      record_currency_code: formData.record_currency_code,
    });

    if (result.success) {
      onClose();
    }
  };

  const getIcon = (lightIcon: string, darkIcon: string) => {
    return isDark ? darkIcon : lightIcon;
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-700 bg-opacity-50 p-4"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-light rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-6 border-b border-grey_light">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-dark">Edit Record</h3>
              <p className="text-grey_dark mt-1">Update record details</p>
            </div>
            <button
              onClick={onClose}
              className="text-grey_dark hover:text-dark p-2"
              aria-label="Close"
              disabled={isLoading}
            >
              <img
                src={closeIcon}
                alt="Close"
                className="w-6 h-6"
              />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-dark mb-2">
                Record Name *
              </label>
              <Input
                type="text"
                attributes={{
                  placeholder: "Enter record name",
                  value: formData.record_name || '',
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('record_name', e.target.value),
                  disabled: isLoading,
                  maxLength: 100
                }}
                // error={!!errors.record_name}
              />
              {errors.record_name && (
                <p className="text-danger text-sm mt-1">{errors.record_name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-dark mb-2">
                Description
              </label>
              <TextArea
                attributes={{
                  placeholder: "Enter record description (optional)",
                  value: formData.record_description || '',
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('record_description', e.target.value),
                  disabled: isLoading,
                  rows: 3,
                  maxLength: 500
                }}
              />
              {errors.record_description && (
                <p className="text-danger text-sm mt-1">{errors.record_description}</p>
              )}
              <p className="text-grey_dark text-xs mt-1">
                {formData.record_description?.length || 0}/500 characters
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark mb-2">
                Currency
              </label>
              <CurrencySelect
                value={formData.record_currency_code || 'USD'}
                onChange={(value) => handleInputChange('record_currency_code', value)}
                placeholder="Select currency"
                disabled={isLoading}
                className="text-sm"
                showCommonFirst={true}
                showFlags={true}
                searchable={true}
              />
            </div>

            <div className="border-t border-grey_light pt-4">
              <div className="bg-bg_grey_light p-3 rounded-lg">
                <h4 className="font-medium text-dark mb-2">Record Information</h4>
                <div className="text-sm text-grey_dark space-y-1">
                  <div className="flex justify-between">
                    <span>Created:</span>
                    <span className="font-medium">
                      {record.created_at 
                        ? new Date(record.created_at).toLocaleDateString() 
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Invite Code:</span>
                    <span className="font-mono font-medium">
                      {record.record_invite_code || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Record ID:</span>
                    <span className="font-medium">{record.id || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t border-grey_light bg-bg_grey_light">
          <div className="flex justify-end space-x-3">
            <Button
              onClick={onClose}
              variant="bordered-primary"
              className="px-4 py-2"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="filled-primary"
              className="px-4 py-2"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRecordModal;